import { WarningIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Heading,
  Image,
  Link,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppConfig } from "../../hooks/useAppConfig";
import { CheckoutType } from "../../app/types/checkoutType";
import { decodeHtmlCharCodes } from "../../utils/stringCodeFormatter";

// Tipi per gestione errori checkout
type ErrorType = 'credit' | 'auth' | 'server' | 'network' | 'timeout' | 'generic';

type ErrorAction = {
  type: 'retry' | 'goToCheckout' | 'goToCatalog' | 'contactSupport' | 'relogin';
  label: string;
  handler: () => void;
  primary?: boolean;
};

type CheckoutError = {
  type: ErrorType;
  status: string;
  message: string;
  originalMessage?: string;
  recoverable: boolean;
  actions: ErrorAction[];
};

type CheckoutErrorSectionProps = {
  error: any; // Tipo errore originale da useCheckout
  checkout: CheckoutType;
};

export const CheckoutErrorSection = ({ error, checkout }: CheckoutErrorSectionProps) => {
  const navigate = useNavigate();
  const { ctaColor, ctaTextColor, primaryColor, secondaryColor } = useAppConfig();

  // Mapping intelligente errore → tipo specifico
  const mapErrorToType = (error: any): ErrorType => {
    const message = error?.message || error?.msg || '';
    
    if (message.includes('420') || message.includes('credito') || message.includes('credit')) {
      return 'credit';
    }
    if (message.includes('401') || message.includes('403') || message.includes('auth')) {
      return 'auth';
    }
    if (message.includes('500') || message.includes('server')) {
      return 'server';
    }
    if (message.includes('Network') || message.includes('ERR_NETWORK') || message.includes('connessione')) {
      return 'network';
    }
    if (message.includes('timeout') || message.includes('ERR_TIMEOUT')) {
      return 'timeout';
    }
    return 'generic';
  };

  // Creazione azioni specifiche per tipo errore
  const createErrorActions = (type: ErrorType): ErrorAction[] => {
    const actions: ErrorAction[] = [];

    switch (type) {
      case 'credit':
        actions.push(
          {
            type: 'contactSupport',
            label: 'Contatta Supporto',
            handler: () => window.open('mailto:ordini@wishlist.it?subject=Problema credito ordine', '_blank'),
            primary: true
          },
          {
            type: 'goToCatalog',
            label: 'Vai al Catalogo',
            handler: () => navigate(-2)
          }
        );
        break;
        
      case 'auth':
        actions.push(
          {
            type: 'relogin',
            label: 'Rieffettua Login',
            handler: () => {
              // Clear session e ritorna alla home per nuovo login
              localStorage.clear();
              sessionStorage.clear();
              window.location.href = '/';
            },
            primary: true
          }
        );
        break;
        
      case 'server':
        actions.push(
          {
            type: 'retry',
            label: 'Riprova',
            handler: () => navigate(-1),
            primary: true
          },
          {
            type: 'contactSupport',
            label: 'Contatta Supporto',
            handler: () => window.open('tel:0236638510', '_blank')
          }
        );
        break;
        
      case 'network':
      case 'timeout':
        actions.push(
          {
            type: 'retry',
            label: 'Riprova',
            handler: () => navigate(-1),
            primary: true
          },
          {
            type: 'goToCheckout',
            label: 'Torna al Checkout',
            handler: () => navigate(-1)
          }
        );
        break;
        
      case 'generic':
      default:
        actions.push(
          {
            type: 'goToCheckout',
            label: 'Torna al Checkout',
            handler: () => navigate(-1),
            primary: true
          },
          {
            type: 'contactSupport',
            label: 'Contatta Supporto',
            handler: () => window.open('mailto:ordini@wishlist.it?subject=Errore ordine', '_blank')
          }
        );
        break;
    }

    return actions;
  };

  // Messaggi specifici per tipo errore
  const getErrorMessage = (type: ErrorType): string => {
    switch (type) {
      case 'credit':
        return 'Credito insufficiente per completare l\'ordine. Verifica il tuo saldo sul portale Wellwork.';
      case 'auth':
        return 'La tua sessione è scaduta. Effettua nuovamente l\'accesso per continuare.';
      case 'server':
        return 'Si è verificato un errore del server. Contatta il supporto.';
      case 'network':
        return 'Errore di connessione. Verifica la tua connessione internet e riprova.';
      case 'timeout':
        return 'La richiesta ha impiegato troppo tempo. Riprova tra qualche istante.';
      case 'generic':
      default:
        return 'Si è verificato un errore imprevisto durante l\'elaborazione dell\'ordine.';
    }
  };

  const errorType = mapErrorToType(error);
  const errorMessage = getErrorMessage(errorType);
  const actions = createErrorActions(errorType);

  return (
    <Box p={"1em"} >
      {/* Header con errore */}
      <VStack spacing={4} mb="2em">
        <WarningIcon boxSize="12" color="red.500" />
        <Heading
          as="h1"
          lineHeight={1.1}
          fontSize={{ base: "2xl", sm: "3xl" }}
          textAlign="center"
          color="red.500"
        >
          Ordine non completato
        </Heading>
        
        {/* Alert con messaggio errore specifico */}
        <Alert status="error" borderRadius="md" maxW="2xl">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Problema durante l'elaborazione</AlertTitle>
            <AlertDescription display="block" mt={2}>
              {errorMessage}
            </AlertDescription>
          </Box>
        </Alert>
      </VStack>

      {/* Dettagli ordine per contesto */}
      {checkout.prodotto && (
        <Box mb="2em">
          <Heading as="h3" fontSize="lg" mb={3} textAlign="center">
            Dettagli ordine non completato:
          </Heading>
          <Stack
            w={"100%"}
            border={"1px solid #d3d3d394"}
            borderRadius="md"
            direction={["column", "column", "row"]}
            bg="gray.50"
          >
            <Center p="1em">
              <Image
                boxSize={["100px", "120px", "140px"]}
                src={`${checkout.prodotto?.baseURL}${checkout.prodotto?.imagename}`}
                alt={decodeHtmlCharCodes(checkout.prodotto?.descrizione)}
                borderRadius="md"
              />
              <Box w={["xs", "md"]} p={2}>
                <Heading fontSize={["sm", "md"]} mb="0.5em">
                  {decodeHtmlCharCodes(checkout.prodotto?.productname)}
                </Heading>
                <Text fontSize={["xs", "sm"]} color="gray.600">
                  {decodeHtmlCharCodes(checkout.prodotto?.descrizione)}
                </Text>
              </Box>
            </Center>
          </Stack>
        </Box>
      )}

      {/* Azioni disponibili */}
      <VStack spacing={4} mb="2em">
        <Text textAlign="center" fontSize="md" fontWeight="semibold">
          Cosa puoi fare ora:
        </Text>
        <Stack direction={["column", "row"]} spacing={3} justify="center">
          {actions.map((action, index) => (
            <Button
              key={action.type}
              onClick={action.handler}
              bgColor={action.primary ? ctaColor : "transparent"}
              color={action.primary ? ctaTextColor : ctaColor}
              borderColor={ctaColor}
              variant={action.primary ? "solid" : "outline"}
              size="md"
              _hover={{ 
                opacity: 0.8,
                transform: "translateY(-1px)"
              }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      </VStack>

      {/* Informazioni di supporto */}
      <Alert status="info" bgColor={secondaryColor} borderRadius="md">
        <AlertIcon color={ctaTextColor} />
        <Box flex="1">
          <AlertTitle color={ctaTextColor}>
            Hai bisogno di aiuto?
          </AlertTitle>
          <AlertDescription color={ctaTextColor} display="block">
            Per assistenza chiamaci al{" "}
            <Link fontWeight="bold" href="tel:0236638510">
              02 3663 8510
            </Link>{" "}
            o scrivici a{" "}
            <Link fontWeight="bold" href="mailto:ordini@wishlist.it">
              ordini@wishlist.it
            </Link>
            .
          </AlertDescription>
        </Box>
      </Alert>

      {/* Debug info (solo in development) */}
      {process.env.NODE_ENV === 'development' && (
        <Box mt={4} p={3} bg="gray.100" borderRadius="md" fontSize="xs">
          <Text fontWeight="bold">Debug Info:</Text>
          <Text>Error Type: {errorType}</Text>
          <Text>Original Message: {error?.message || error?.msg || 'N/A'}</Text>
        </Box>
      )}
    </Box>
  );
}; 